import PropTypes from 'prop-types';
import { m } from 'framer-motion';

// @mui
import { alpha, styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';

//
import Logo from './Logo';
import ProgressBar from './ProgressBar';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  position: 'fixed',
  inset: 0,
  zIndex: 99999,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  background: `
    radial-gradient(circle at top, ${alpha(theme.palette.primary.main, 0.14)} 0%, transparent 45%),
    ${theme.palette.background.default}
  `,
}));

const CardStyle = styled(m.div)(({ theme }) => ({
  position: 'relative',
  width: 340,
  height: 140,
  borderRadius: 32,
  overflow: 'hidden',
  backdropFilter: 'blur(18px)',
  background: alpha(theme.palette.background.paper, 0.72),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
  boxShadow: `
    0 12px 40px ${alpha(theme.palette.common.black, 0.18)},
    inset 0 1px 1px ${alpha(theme.palette.common.white, 0.08)}
  `,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

// ----------------------------------------------------------------------

LoadingScreen.propTypes = {
  isDashboard: PropTypes.bool,
};

export default function LoadingScreen({ isDashboard, ...other }) {
  return (
    <>
      <ProgressBar />

      {!isDashboard && (
        <RootStyle {...other}>
          {/* Glow background */}
          <Box
            component={m.div}
            animate={{
              scale: [1, 1.15, 1],
              opacity: [0.15, 0.28, 0.15],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            sx={(theme) => ({
              position: 'absolute',
              width: 500,
              height: 500,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${alpha(
                theme.palette.primary.main,
                0.18
              )} 0%, transparent 70%)`,
            })}
          />

          {/* Main Card */}
          <CardStyle
            initial={{ opacity: 0, y: 15 }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.45,
            }}
          >
            {/* Animated line */}
            <Box
              component={m.div}
              animate={{
                x: ['-120%', '220%'],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'linear',
              }}
              sx={(theme) => ({
                position: 'absolute',
                top: 0,
                left: 0,
                width: 120,
                height: '100%',
                background: `linear-gradient(
                  90deg,
                  transparent,
                  ${alpha(theme.palette.primary.light, 0.18)},
                  transparent
                )`,
                transform: 'skewX(-20deg)',
              })}
            />

            {/* Logo */}
            <Box
              component={m.div}
              animate={{
                scale: [1, 1.03, 1],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              sx={{
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Logo width={280} height={70} disabledLink />

              <Typography
                variant="caption"
                sx={(theme) => ({
                  mt: 1,
                  letterSpacing: 4,
                  fontWeight: 700,
                  color: alpha(theme.palette.text.primary, 0.45),
                })}
              >
                LOADING
              </Typography>
            </Box>
          </CardStyle>
        </RootStyle>
      )}
    </>
  );
}